import React, { useState, useEffect, useRef, createContext, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { GameCard } from '../components/GameCard';
import { useAuth } from '../contexts/AuthContext';
import { 
  loadOnboardingGames, 
  markOnboardingAsCompleted 
} from '../lib/services/onboardingService';
import { ONBOARDING_CONFIG } from '../config/onboarding';
import { Game as AppGame, UserGame } from '../types';
import { 
  toggleOwned,
  toggleWishlist,
  togglePlayed,
  updatePlayCount
} from '../lib/game/collectionService';
import { rateGame } from '../lib/game/ratingService';
import toast from 'react-hot-toast';
import { supabase } from '../lib/supabase';
import { 
  Archive, 
  Heart, 
  BookOpen, 
  BarChart2, 
  Search, 
  ThumbsUp, 
  Users, 
  ShoppingCart, 
  Sparkles,
  ArrowRight,
  CheckCircle2,
  ChevronRight,
  XCircle,
  ArrowRightCircle,
  Gamepad2,
  Dice4,
  PlusCircle,
  Loader2,
  Zap,         // Para Streaks
  Star,        // Para XP/Níveis ou Badges
  Award,       // Para Missões ou Badges
  ShieldCheck, // Para Mascotes ou Badges
  UserCheck,    // Para Mascotes (companheiro)
} from 'lucide-react';
import { motion } from 'framer-motion';
import SEO from '../components/SEO';
import { 
  getAllMascots, 
  chooseMascot, 
  type Mascot as MascotType // Renomeado para evitar conflito se houver outro Mascot
} from '../lib/gamification/mascotService';
import { useTheme } from '../contexts/ThemeContext';

// Criando um contexto para o status de onboarding
interface OnboardingStatusContextType {
  onboardingCompleted: boolean | null;
  setOnboardingCompleted: React.Dispatch<React.SetStateAction<boolean | null>>;
}

const OnboardingStatusContext = createContext<OnboardingStatusContextType | null>(null);

// Provider que deve envolver a aplicação
export const OnboardingStatusProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [onboardingCompleted, setOnboardingCompleted] = useState<boolean | null>(null);
  
  return (
    <OnboardingStatusContext.Provider value={{ onboardingCompleted, setOnboardingCompleted }}>
      {children}
    </OnboardingStatusContext.Provider>
  );
};

// Hook para acessar o contexto
export const useOnboardingStatus = () => {
  const context = useContext(OnboardingStatusContext);
  if (!context) {
    throw new Error('useOnboardingStatus deve ser usado dentro de um OnboardingStatusProvider');
  }
  return context;
};

/**
 * Página de onboarding para novos usuários
 */
const OnboardingPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { theme } = useTheme();
  
  const [games, setGames] = useState<AppGame[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [step, setStep] = useState(1); // Agora 5 passos: 1=welcome, 2=platform_features, 3=gamification, 4=mascot, 5=games
  const [userGames, setUserGames] = useState<Map<string, { owned: boolean, wishlist: boolean, played: boolean, playCount: number, rating: number }>>(new Map());
  const [interactionCount, setInteractionCount] = useState(0);
  const [onboardingMarkedAsCompleted, setOnboardingMarkedAsCompleted] = useState(false);
  
  // Estados para Mascotes
  const [mascotsList, setMascotsList] = useState<MascotType[]>([]);
  const [selectedMascotId, setSelectedMascotId] = useState<string | null>(null);
  const [loadingMascots, setLoadingMascots] = useState(false);
  const [savingMascot, setSavingMascot] = useState(false);
  const [mascotError, setMascotError] = useState<string | null>(null);
  const [currentUserMascot, setCurrentUserMascot] = useState<MascotType | null>(null);
  const [loadingUserMascot, setLoadingUserMascot] = useState(false);
  
  const onboardingStatusMarked = useRef(false);
  const { setOnboardingCompleted } = useOnboardingStatus();
  
  useEffect(() => {
    // A lógica de marcar como concluído foi movida para a função completeOnboarding
    // para garantir que só aconteça no final do fluxo ou ao pular tudo.
  }, [user, onboardingMarkedAsCompleted, setOnboardingCompleted]);
  
  useEffect(() => {
    const fetchGamesForOnboarding = async () => {
      // Carregar jogos apenas quando estiver no passo de seleção de jogos (novo Passo 5)
      if (step === 5 && user) {
      try {
        setLoading(true);
        const onboardingGames = await loadOnboardingGames();
        setGames(onboardingGames);
          if (onboardingGames.length > 0) {
          await fetchUserGameStates(onboardingGames, user.id);
        }
        } catch (err) {
          console.error("[ONBOARDING] Erro ao carregar jogos:", err);
          setError("Não foi possível carregar os jogos sugeridos.");
      } finally {
        setLoading(false);
        }
      }
    };
    
    fetchGamesForOnboarding();
  }, [user, step]);
  
  useEffect(() => {
    const fetchMascotDataForOnboardingStep = async () => {
      // Carregar dados de mascote apenas no passo de escolha de mascote (novo Passo 4)
      if (step === 4 && user) {
        setLoadingMascots(true);
        setLoadingUserMascot(true);
        setMascotError(null);
        try {
          const mascots = await getAllMascots(supabase);
          setMascotsList(mascots);

          const { data: userMascotData, error: userMascotError } = await supabase
            .from('user_mascots')
            .select('mascot_id')
            .eq('user_id', user.id)
            .single();

          if (userMascotError && userMascotError.code !== 'PGRST116') {
            throw userMascotError;
          }

          if (userMascotData?.mascot_id) {
            const { data: mascotData, error: mascotError } = await supabase
              .from('mascots')
              .select(`
                *,
                levels: mascot_levels (
                  id,
                  mascot_id,
                  level_number,
                  min_user_level,
                  image_url,
                  created_at
                )
              `)
              .eq('id', userMascotData.mascot_id)
              .single();
            
            if (mascotError) throw mascotError;

            if (mascotData) {
              let processedLevels = (mascotData.levels || []).map((level: any) => ({ ...level }));
              processedLevels.sort((a: any, b: any) => a.level_number - b.level_number);
              const completeMascotData: MascotType = {
                id: mascotData.id,
                name: mascotData.name,
                description: mascotData.description,
                is_active: mascotData.is_active,
                category: mascotData.category,
                levels: processedLevels as MascotType['levels'],
              };
              setCurrentUserMascot(completeMascotData);
            } else {
              setCurrentUserMascot(null);
            }
          } else {
            setCurrentUserMascot(null);
          }
        } catch (err) {
          console.error("[ONBOARDING] Erro ao carregar dados de mascotes:", err);
          setMascotError("Erro ao carregar informações dos mascotes.");
          toast.error("Erro ao buscar mascotes.");
        } finally {
          setLoadingMascots(false);
          setLoadingUserMascot(false);
        }
      }
    };

    fetchMascotDataForOnboardingStep();
  }, [step, user]);
  
  const fetchUserGameStates = async (gamesList: AppGame[], userId: string) => {
    try {
      const { data, error } = await supabase
        .from('user_games')
        .select('*')
        .eq('user_id', userId)
        .in('game_id', gamesList.map(game => game.id));
        
      if (error) {
        console.error("[ONBOARDING] Erro ao buscar estados dos jogos:", error);
        return;
      }
      
      const newUserGames = new Map<string, { owned: boolean, wishlist: boolean, played: boolean, playCount: number, rating: number }>();
      
      if (data && data.length > 0) {
        data.forEach((userGame: any) => {
          newUserGames.set(userGame.game_id, {
            owned: userGame.owned || false,
            wishlist: userGame.wishlist || false,
            played: (userGame.play_count ?? 0) > 0,
            playCount: userGame.play_count ?? 0,
            rating: userGame.rating || 0
          });
        });
        setUserGames(newUserGames);
      }
    } catch (error) {
      console.error("[ONBOARDING] Erro ao buscar estados dos jogos:", error);
    }
  };
  
  const handleContinue = async () => {
    if (step < 5) { // Temos 5 passos agora
      setStep(prevStep => prevStep + 1);
    } else if (step === 5) { // Último passo (seleção de jogos)
      await completeOnboarding();
    }
  };
  
  // Pula todo o onboarding
  const handleSkipAll = async () => {
    await completeOnboarding();
  };

  // Pula o passo atual e vai para o próximo (usado nos passos intermediários)
  const handleSkipToNextStep = () => {
    if (step < 5) {
        setStep(prevStep => prevStep + 1);
    } else {
        completeOnboarding(); // Se estiver no último passo e pular, completa
    }
  };
  
  const completeOnboarding = async () => {
    if (user) {
      try {
        if (!onboardingStatusMarked.current) {
          onboardingStatusMarked.current = true;
          const success = await markOnboardingAsCompleted(user.id);
          if (success) {
            setOnboardingMarkedAsCompleted(true);
            setOnboardingCompleted(true);
            sessionStorage.setItem('onboarding_completed', 'true');
            sessionStorage.setItem(`onboarding_status_${user.id}`, 'completed');
          } else {
            setOnboardingCompleted(true);
            sessionStorage.setItem('onboarding_completed', 'true');
          }
        }
        navigate('/');
      } catch (error) {
        console.error("[ONBOARDING] Erro ao completar onboarding:", error);
        setOnboardingCompleted(true);
        sessionStorage.setItem('onboarding_completed', 'true');
        navigate('/');
      }
    } else {
      navigate('/');
    }
  };
  
  const handleToggleOwned = async (gameId: string, isOwned: boolean) => {
    if (!user) return;
    try {
      await toggleOwned(user.id, gameId, isOwned);
      if (games.length > 0) await fetchUserGameStates(games, user.id);
      trackInteraction();
    } catch (error) { toast.error("Erro ao atualizar coleção"); }
  };
  
  const handleToggleWishlist = async (gameId: string, isWishlist: boolean) => {
    if (!user) return;
    try {
      await toggleWishlist(user.id, gameId, isWishlist);
      if (games.length > 0) await fetchUserGameStates(games, user.id);
      trackInteraction();
    } catch (error) { toast.error("Erro ao atualizar lista de desejos"); }
  };
  
  const handleTogglePlayed = async (gameId: string, isPlayed: boolean) => {
    if (!user) return;
    try {
      await togglePlayed(user.id, gameId, isPlayed);
      if (games.length > 0) await fetchUserGameStates(games, user.id);
      trackInteraction();
    } catch (error) { toast.error("Erro ao atualizar jogos jogados"); }
  };
  
  const handleUpdatePlayCount = async (gameId: string, count: number) => {
    if (!user) return;
    try {
      await updatePlayCount(user.id, gameId, count);
      if (games.length > 0) await fetchUserGameStates(games, user.id);
      trackInteraction();
    } catch (error) { toast.error("Erro ao atualizar contagem de jogadas"); }
  };
  
  const handleRateGame = async (gameId: string, rating: number) => {
    if (!user) return;
    try {
      await rateGame(user.id, gameId, rating);
      if (games.length > 0) await fetchUserGameStates(games, user.id);
      trackInteraction();
    } catch (error) { toast.error("Erro ao avaliar jogo"); }
  };
  
  const trackInteraction = () => setInteractionCount(prev => prev + 1);

  const ProgressIndicator = ({ currentStep, totalSteps = 5 }: { currentStep: number, totalSteps?: number }) => {
    return (
      <div className="flex items-center justify-center mb-8">
        <div className="flex items-center">
          {Array.from({ length: totalSteps }).map((_, index) => (
            <React.Fragment key={index}>
              <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 ${currentStep >= index + 1 ? (theme === 'dark' ? 'bg-primary text-primary-foreground' : 'bg-indigo-600 text-white') : (theme === 'dark' ? 'bg-muted text-muted-foreground' : 'bg-gray-200 text-gray-500')}`}>
                {index + 1}
          </div>
              {index < totalSteps - 1 && (
                <div className={`w-12 h-1 transition-all duration-300 ${currentStep > index + 1 ? (theme === 'dark' ? 'bg-primary' : 'bg-indigo-600') : (theme === 'dark' ? 'bg-muted' : 'bg-gray-200')}`}></div>
              )}
            </React.Fragment>
          ))}
        </div>
      </div>
    );
  };
  
  const handleSelectMascot = (mascotId: string) => setSelectedMascotId(mascotId);

  const handleConfirmMascotAndContinue = async () => {
    if (!user || !selectedMascotId) {
      toast.error("Por favor, selecione um mascote antes de continuar.");
      return;
    }
    setSavingMascot(true);
    setMascotError(null);
    try {
      const result = await chooseMascot(supabase, user.id, selectedMascotId, 1);
      if (result.success) {
        toast.success("Mascote escolhido com sucesso!");
        handleContinue(); // Avança para o próximo passo
      } else {
        setMascotError(result.error || "Ocorreu um erro ao salvar sua escolha.");
        toast.error(result.error || "Erro ao escolher mascote.");
      }
    } catch (err) {
      setMascotError("Ocorreu um erro inesperado. Tente novamente.");
      toast.error("Erro ao confirmar mascote.");
    } finally {
      setSavingMascot(false);
    }
  };
  
  // Render step 1: Welcome
  if (step === 1) {
    return (
      <>
        <SEO 
          title="Bem-vindo ao Boardverso"
          description="Complete o processo de onboarding para personalizar sua experiência."
        />
        <div className={`min-h-screen py-12 px-4 sm:px-6 lg:px-8 ${theme === 'dark' ? 'text-foreground' : 'bg-gradient-to-br from-indigo-50 to-purple-50'}`}>
          <div className="max-w-3xl mx-auto">
            <ProgressIndicator currentStep={1} />
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className={`${theme === 'dark' ? 'bg-gray-800 border border-border' : 'bg-white'} rounded-xl shadow-xl overflow-hidden`}
            >
              <div className={`${theme === 'dark' ? 'bg-primary' : 'bg-indigo-600'} h-2`}></div>
              <div className="p-8">
                <div className="flex justify-center mb-6">
                  <div className={`${theme === 'dark' ? 'bg-primary/20' : 'bg-indigo-100'} w-20 h-20 rounded-full flex items-center justify-center`}>
                    <Dice4 className={`h-10 w-10 ${theme === 'dark' ? 'text-primary' : 'text-indigo-600'}`} />
                  </div>
                </div>
                <h1 className={`text-4xl font-bold text-center mb-6 ${theme === 'dark' ? 'text-card-foreground' : 'text-gray-900'}`}>
                  {ONBOARDING_CONFIG.texts.welcome.title}
                </h1>
                <div className="mb-8 text-center">
                  <p className={`text-xl leading-relaxed max-w-2xl mx-auto ${theme === 'dark' ? 'text-muted-foreground' : 'text-gray-600'}`}>
                    {ONBOARDING_CONFIG.texts.welcome.description}
                  </p>
                </div>
                <div className="flex flex-col items-center justify-center space-y-4">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={handleContinue}
                    className={`w-full sm:w-auto px-8 py-4 text-lg font-medium rounded-xl shadow-md hover:shadow-lg transition-all flex items-center justify-center gap-2 ${theme === 'dark' ? 'bg-primary text-primary-foreground hover:bg-primary/90' : 'text-white bg-indigo-600 hover:bg-indigo-700'}`}
                  >
                    Vamos começar <ArrowRight className="ml-1 h-5 w-5" />
                  </motion.button>
                  <button
                    onClick={handleSkipAll} // Agora pula tudo
                    className={`text-sm transition-colors mt-4 ${theme === 'dark' ? 'text-muted-foreground hover:text-foreground' : 'text-gray-500 hover:text-gray-700'}`}
                  >
                    {ONBOARDING_CONFIG.texts.skipText} (Pular tudo)
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </>
    );
  }
  
  // Render step 2: Platform Features (Reintroduzido)
  if (step === 2) {
    return (
      <>
        <SEO 
          title="Conheça o Boardverso"
          description="Descubra os recursos da nossa plataforma de jogos de tabuleiro."
        />
        <div className={`min-h-screen py-8 px-4 sm:px-6 lg:px-8 ${theme === 'dark' ? ' text-foreground' : 'bg-gradient-to-br from-indigo-50 to-purple-50'}`}>
          <div className="max-w-5xl mx-auto">
            <ProgressIndicator currentStep={2} />
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className={`${theme === 'dark' ? 'bg-gray-800 border border-border' : 'bg-white'} rounded-xl shadow-xl overflow-hidden`}
            >
              <div className={`${theme === 'dark' ? 'bg-primary' : 'bg-indigo-600'} h-2`}></div>
              <div className="p-8">
                <h1 className={`text-3xl font-bold text-center mb-4 ${theme === 'dark' ? 'text-card-foreground' : 'text-gray-900'}`}>
                  Conheça Nossa Plataforma
                </h1>
                <div className="mb-8 text-center">
                  <p className={`text-lg max-w-3xl mx-auto ${theme === 'dark' ? 'text-muted-foreground' : 'text-gray-600'}`}>
                    Descubra todas as ferramentas e recursos que oferecemos para aprimorar sua experiência com jogos de tabuleiro!
                  </p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10">
                  {[ // Array de features para mapear
                    { icon: BookOpen, title: "Gerencie Sua Coleção", text: "Adicione jogos à sua coleção e veja estatísticas detalhadas sobre categorias, mecânicas e temas preferidos. Acompanhe quantos jogos da sua coleção você já jogou.", themeColor: "indigo" },
                    { icon: Heart, title: "Lista de Desejos", text: "Mantenha uma lista de jogos que você deseja adquirir. Outros usuários poderão ver seu interesse, facilitando convites ou ofertas.", themeColor: "red" },
                    { icon: PlusCircle, title: "Jogos Jogados", text: "Marque os jogos que você já experimentou, mesmo sem possuí-los, para um histórico completo e melhores recomendações.", themeColor: "blue" },
                    { icon: BarChart2, title: "Rankings Personalizados", text: "Explore rankings de jogos por popularidade, categorias, mecânicas e mais. Descubra os favoritos da comunidade.", themeColor: "purple" },
                    { icon: Search, title: "Busca Avançada", text: "Encontre o jogo perfeito com filtros por jogadores, tempo, complexidade, categorias, temas e mecânicas.", themeColor: "green" },
                    { icon: ThumbsUp, title: "Recomendações Personalizadas", text: "Receba sugestões de jogos baseadas nos seus gostos. Avalie as recomendações para refinar futuras sugestões.", themeColor: "yellow" },
                    { icon: Users, title: "Comunidade Ativa", text: "Conecte-se com outros jogadores, adicione amigos, troque mensagens e organize partidas.", themeColor: "pink" },
                    { icon: ShoppingCart, title: "Mercado de Jogos", text: "Anuncie jogos para vender ou comprar. Negocie diretamente com outros membros da comunidade.", themeColor: "orange" },
                    { icon: Sparkles, title: "Sempre Evoluindo", text: "Estamos constantemente adicionando novos recursos e melhorando a plataforma. Fique atento às novidades!", themeColor: "cyan", colSpan: "md:col-span-2" }
                  ].map(feature => {
                    const bgColorClass = theme === 'dark' ? `bg-${feature.themeColor}-700/20` : `bg-${feature.themeColor}-100`;
                    const textColorClass = theme === 'dark' ? `text-${feature.themeColor}-400` : `text-${feature.themeColor}-600`;
                    const cardBgColorClass = theme === 'dark' ? `bg-gray-800 border border-${feature.themeColor}-800/50 hover:border-${feature.themeColor}-700/70` : `bg-white border border-gray-100`;
                    const titleColorClass = theme === 'dark' ? `text-card-foreground` : `text-gray-900`;
                    const pColorClass = theme === 'dark' ? `text-muted-foreground` : `text-gray-600`;

                    return (
                      <motion.div
                        key={feature.title}
                        whileHover={{ y: -5 }}
                        className={`flex flex-col rounded-xl shadow-sm hover:shadow-md transition-all overflow-hidden ${cardBgColorClass} ${feature.colSpan || ''}`}
                      >
                        <div className={`flex justify-center ${bgColorClass} py-6`}>
                          <feature.icon className={`h-12 w-12 ${textColorClass}`} />
                        </div>
                        <div className="p-6">
                          <h3 className={`text-xl font-semibold mb-2 ${titleColorClass}`}>{feature.title}</h3>
                          <p className={pColorClass}>{feature.text}</p>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-8">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={handleContinue}
                    className={`w-full sm:w-auto px-8 py-4 text-lg font-medium rounded-xl shadow-md hover:shadow-lg transition-all flex items-center justify-center ${theme === 'dark' ? 'bg-primary text-primary-foreground hover:bg-primary/90' : 'text-white bg-indigo-600 hover:bg-indigo-700'}`}
                  >
                    Entendido, próximo! <ChevronRight className="ml-2 h-5 w-5" />
                  </motion.button>
                  <button onClick={handleSkipAll} className={`text-sm transition-colors ${theme === 'dark' ? 'text-muted-foreground hover:text-foreground' : 'text-gray-500 hover:text-gray-700'}`}>
                    {ONBOARDING_CONFIG.texts.skipText} (Pular tudo)
                  </button>
                    </div>
                    </div>
                  </motion.div>
                    </div>
                    </div>
      </>
    );
  }
  
  // Render step 3: Gamification Explanation (Antigo Passo 2)
  if (step === 3) {
    return (
      <>
        <SEO 
          title="Entenda a Gamificação - Boardverso"
          description="Descubra como XP, níveis, streaks, missões e mascotes tornam sua jornada mais divertida."
        />
        <div className={`min-h-screen py-8 px-4 sm:px-6 lg:px-8 ${theme === 'dark' ? 'text-foreground' : 'bg-gradient-to-br from-purple-50 to-pink-50'}`}>
          <div className="max-w-4xl mx-auto">
            <ProgressIndicator currentStep={3} />
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className={`${theme === 'dark' ? 'bg-gray-800 border border-border' : 'bg-white'} rounded-xl shadow-xl overflow-hidden`}
            >
              <div className={`${theme === 'dark' ? 'bg-purple-500' : 'bg-purple-600'} h-2`}></div>
              <div className="p-8">
                <h1 className={`text-3xl font-bold text-center mb-4 ${theme === 'dark' ? 'text-card-foreground' : 'text-gray-900'}`}>
                  Sua Jornada Gamificada!
                </h1>
                <p className={`text-lg text-center max-w-2xl mx-auto mb-10 ${theme === 'dark' ? 'text-muted-foreground' : 'text-gray-600'}`}>
                  No Boardverso, sua paixão por jogos de tabuleiro é recompensada! Veja como:
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10">
                  {[ // Array de features de gamificação
                    { icon: Star, title: 'XP e Níveis', text: 'Ganhe XP por suas atividades (adicionar jogos, registrar partidas, etc.) e suba de nível, desbloqueando novos ranks!', color: 'yellow' },
                    { icon: Zap, title: 'Streaks Diários', text: 'Mantenha uma sequência de logins diários para ganhar bônus de XP e proteger seu progresso.', color: 'orange' },
                    { icon: Award, title: 'Missões e Desafios', text: 'Complete missões variadas para ganhar XP extra, badges exclusivos e outras recompensas.', color: 'teal' },
                    { icon: ShieldCheck, title: 'Badges de Conquista', text: 'Colecione badges por suas realizações, mostrando suas habilidades e dedicação.', color: 'green' },
                    { icon: UserCheck, title: 'Mascotes Companheiros', text: 'Escolha um mascote que evolui com você, refletindo seu progresso e estilo de jogo!', color: 'indigo' },
                    { icon: Users, title: 'Indique Amigos', text: 'Convide amigos para o Boardverso com seu link de indicação (disponível nas Configurações do Perfil) e ganhem recompensas juntos!', color: 'pink' }
                  ].map(item => {
                    const cardBgColorClass = theme === 'dark' ? `bg-${item.color}-600/10 border-${item.color}-500/30 hover:border-${item.color}-500/50` : `bg-${item.color}-50 border-${item.color}-200`;
                    const iconBgClass = theme === 'dark' ? `bg-${item.color}-500/20` : `bg-${item.color}-100`;
                    const iconTextClass = theme === 'dark' ? `text-${item.color}-400` : `text-${item.color}-600`;
                    const titleTextClass = theme === 'dark' ? `text-${item.color}-300` : `text-${item.color}-800`;
                    const pTextClass = theme === 'dark' ? `text-muted-foreground` : `text-gray-700`;
                    return (
                      <motion.div key={item.title} whileHover={{ y: -5 }} className={`flex flex-col rounded-xl border shadow-sm hover:shadow-md transition-all p-6 ${cardBgColorClass}`}>
                        <div className={`flex items-center justify-center w-12 h-12 rounded-full mb-3 ${iconBgClass}`}>
                          <item.icon className={`h-6 w-6 ${iconTextClass}`} />
                        </div>
                        <h3 className={`text-xl font-semibold mb-2 ${titleTextClass}`}>{item.title}</h3>
                        <p className={`text-sm ${pTextClass}`}>{item.text}</p>
                      </motion.div>
                    );
                  })}
                </div>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-8">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={handleContinue}
                    className={`w-full sm:w-auto px-8 py-4 text-lg font-medium rounded-xl shadow-md hover:shadow-lg transition-all flex items-center justify-center ${theme === 'dark' ? 'bg-purple-600 text-white hover:bg-purple-500' : 'text-white bg-purple-600 hover:bg-purple-700'}`}
                  >
                    Entendi, próximo! <ChevronRight className="ml-2 h-5 w-5" />
                  </motion.button>
                  <button onClick={handleSkipToNextStep} className={`text-sm transition-colors ${theme === 'dark' ? 'text-muted-foreground hover:text-foreground' : 'text-gray-500 hover:text-gray-700'}`}>
                    Pular este passo
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </>
    );
  }

  // Render step 4: Mascot Choice (Antigo Passo 3)
  if (step === 4) {
     return (
      <>
        <SEO 
          title="Escolha seu Mascote - Boardverso"
          description="Selecione um mascote para te acompanhar em sua jornada gamificada."
        />
        <div className={`min-h-screen py-8 px-4 sm:px-6 lg:px-8 ${theme === 'dark' ? 'text-foreground' : 'bg-gradient-to-br from-teal-50 to-cyan-50'}`}>
          <div className="max-w-4xl mx-auto">
            <ProgressIndicator currentStep={4} />
                  <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className={`${theme === 'dark' ? 'bg-gray-800 border border-border' : 'bg-white'} rounded-xl shadow-xl overflow-hidden`}
            >
              <div className={`${theme === 'dark' ? 'bg-teal-500' : 'bg-teal-600'} h-2`}></div>
              <div className="p-8">
                <h1 className={`text-3xl font-bold text-center mb-4 ${theme === 'dark' ? 'text-card-foreground' : 'text-gray-900'}`}>
                  Escolha seu Mascote!
                </h1>
                <p className={`text-lg text-center max-w-2xl mx-auto mb-6 ${theme === 'dark' ? 'text-muted-foreground' : 'text-gray-600'}`}>
                  Seu mascote te representa e evolui junto com você. Escolha com sabedoria!
                </p>
                {loadingUserMascot && (
                  <div className="flex justify-center items-center py-5">
                    <Loader2 className={`h-8 w-8 animate-spin ${theme === 'dark' ? 'text-teal-400' : 'text-teal-500'}`} />
                    <p className={`ml-2 ${theme === 'dark' ? 'text-muted-foreground' : 'text-gray-500'}`}>Verificando seu mascote atual...</p>
                    </div>
                )}
                {!loadingUserMascot && currentUserMascot && !selectedMascotId && (
                  <div className={`mb-6 p-4 border rounded-lg text-center ${theme === 'dark' ? 'border-green-700/50 bg-green-600/10' : 'border-green-200 bg-green-50'}`}>
                    <h3 className={`text-lg font-semibold mb-2 ${theme === 'dark' ? 'text-green-400' : 'text-green-700'}`}>Seu Mascote Atual:</h3>
                    <img 
                      src={currentUserMascot.levels.find(l => l.level_number === 1)?.image_url || currentUserMascot.levels[0]?.image_url || 'https://via.placeholder.com/100?text=Mascote'} 
                      alt={currentUserMascot.name} 
                      className={`w-20 h-20 rounded-full object-cover mx-auto mb-2 border-2 ${theme === 'dark' ? 'border-green-600/50' : 'border-green-200'}`}
                    />
                    <p className={`text-xl font-bold ${theme === 'dark' ? 'text-green-300' : 'text-green-800'}`}>{currentUserMascot.name}</p>
                    {currentUserMascot.category && <p className={`text-sm ${theme === 'dark' ? 'text-green-500' : 'text-green-600'}`}>Categoria: {currentUserMascot.category}</p>}
                    <p className={`text-xs mt-2 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>Você pode continuar com este mascote ou escolher um novo abaixo.</p>
                    </div>
                )}
                {loadingMascots && (
                  <div className="flex justify-center items-center py-10">
                    <Loader2 className={`h-12 w-12 animate-spin ${theme === 'dark' ? 'text-teal-400' : 'text-teal-600'}`} />
                    <p className={`ml-3 ${theme === 'dark' ? 'text-muted-foreground' : 'text-gray-600'}`}>Carregando mascotes...</p>
                    </div>
                )}
                {mascotError && (
                  <div className={`${theme === 'dark' ? 'bg-destructive/20 border-destructive' : 'bg-red-50 border-red-400'} border-l-4 p-4 rounded-md mb-6`}>
                    <div className="flex">
                      <div className="flex-shrink-0"><XCircle className={`h-5 w-5 ${theme === 'dark' ? 'text-destructive' : 'text-red-400'}`} /></div>
                      <div className="ml-3"><p className={`text-sm ${theme === 'dark' ? 'text-destructive-foreground' : 'text-red-700'}`}>{mascotError}</p></div>
                    </div>
                  </div>
                )}
                {!loadingMascots && !mascotError && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 mb-10">
                    {mascotsList.map((mascot) => (
                  <motion.div
                        key={mascot.id}
                    whileHover={{ y: -5 }}
                        onClick={() => handleSelectMascot(mascot.id)}
                        className={`p-4 border rounded-lg cursor-pointer transition-all duration-200 shadow-sm hover:shadow-lg 
                                    ${selectedMascotId === mascot.id 
                                      ? (theme === 'dark' ? 'border-teal-500 ring-2 ring-teal-500 bg-teal-700/20' : 'border-teal-500 ring-2 ring-teal-500 bg-teal-50') 
                                      : (theme === 'dark' ? 'border-border bg-gray-800 hover:border-border/70' : 'border-gray-200 bg-white')}`}
                      >
                        <img 
                          src={mascot.levels[0]?.image_url || 'https://via.placeholder.com/150?text=Sem+Imagem'} 
                          alt={mascot.name} 
                          className={`w-24 h-24 rounded-full object-cover mx-auto mb-3 border-2 ${theme === 'dark' ? 'border-muted' : 'border-gray-100'}`}
                        />
                        <h3 className={`text-lg font-semibold text-center mb-1 ${theme === 'dark' ? 'text-card-foreground' : 'text-gray-800'}`}>{mascot.name}</h3>
                        {mascot.category && (
                           <p className={`text-xs text-center px-2 py-0.5 rounded-full inline-block mx-auto ${theme === 'dark' ? 'bg-muted text-muted-foreground' : 'bg-gray-100 text-gray-500'}`}>{mascot.category}</p>
                        )}
                        {mascot.description && (
                          <p className={`text-xs text-center mt-2 px-2 ${theme === 'dark' ? 'text-muted-foreground/80' : 'text-gray-600'}`}>{mascot.description}</p>
                        )}
                        <div className={`mt-3 pt-3 border-t ${theme === 'dark' ? 'border-border' : 'border-gray-100'}`}>
                          <p className={`text-xs text-center mb-1 ${theme === 'dark' ? 'text-muted-foreground' : 'text-gray-500'}`}>Evoluções:</p>
                          <div className="flex justify-center gap-2">
                            {mascot.levels.slice(0, 3).map(level => (
                              <img 
                                key={level.id} 
                                src={level.image_url || 'https://via.placeholder.com/50'} 
                                alt={`Nível ${level.level_number}`}
                                title={`Nível ${level.level_number} (Requer Lv. ${level.min_user_level})`}
                                className={`w-8 h-8 rounded-full object-cover border ${theme === 'dark' ? 'border-muted' : 'border-gray-200'}`}
                              />
                            ))}
                            {mascot.levels.length > 3 && <span className="text-xs self-end">...</span>}
                    </div>
                    </div>
                  </motion.div>
                    ))}
                </div>
                )}
                {mascotsList.length === 0 && !loadingMascots && !mascotError && (
                  <div className={`text-center py-10 border-2 border-dashed rounded-lg mb-10 ${theme === 'dark' ? 'border-border text-muted-foreground' : 'border-gray-300 text-gray-500'}`}>
                    <p>Nenhum mascote disponível no momento.</p>
                  </div>
                )}
                <div className="flex flex-col items-center justify-center gap-4 mt-8">
                  {(() => {
                    let buttonText = "Confirmar Mascote";
                    let buttonAction = handleConfirmMascotAndContinue;
                    let isDisabled = savingMascot;
                    if (currentUserMascot) {
                      if (!selectedMascotId || selectedMascotId === currentUserMascot.id) {
                        buttonText = `Continuar com ${currentUserMascot.name}`;
                        buttonAction = handleContinue;
                        isDisabled = false;
                      } else {
                        const newSelectedMascot = mascotsList.find(m => m.id === selectedMascotId);
                        buttonText = `Confirmar Novo: ${newSelectedMascot?.name || 'Mascote'}`;
                        isDisabled = !selectedMascotId || savingMascot;
                      }
                    } else {
                      isDisabled = !selectedMascotId || savingMascot;
                      if (selectedMascotId){
                        const newSelectedMascot = mascotsList.find(m => m.id === selectedMascotId);
                        buttonText = `Confirmar: ${newSelectedMascot?.name || 'Mascote'}`;
                      } else {
                        buttonText = "Selecione um Mascote";
                      }
                    }
                    return (
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.98 }}
                        onClick={buttonAction}
                        disabled={isDisabled}
                        className={`w-full sm:w-auto px-8 py-4 text-lg font-medium rounded-xl shadow-md hover:shadow-lg transition-all flex items-center justify-center disabled:opacity-60 disabled:cursor-not-allowed ${theme === 'dark' ? 'bg-teal-600 text-white hover:bg-teal-500' : 'bg-teal-600 text-white hover:bg-teal-700'}`}
                  >
                        {savingMascot && buttonAction === handleConfirmMascotAndContinue ? <Loader2 className="h-5 w-5 animate-spin mr-2" /> : null}
                        {buttonText} <ChevronRight className="ml-2 h-5 w-5" />
                  </motion.button>
                    );
                  })()}
                  <button
                    onClick={handleSkipToNextStep} 
                    className={`text-sm transition-colors mt-2 ${theme === 'dark' ? 'text-muted-foreground hover:text-foreground' : 'text-gray-500 hover:text-gray-700'}`}
                  >
                    Pular este passo
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </>
    );
  }
  
  // Render step 5: Select games (Antigo Passo 4)
  if (step === 5) {
  return (
    <>
      <SEO 
          title="Conte-nos sobre seus Jogos - Boardverso"
          description="Adicione alguns jogos à sua coleção ou lista de desejos para começarmos."
      />
      <div className={`min-h-screen py-8 px-4 sm:px-6 lg:px-8 ${theme === 'dark' ? 'text-foreground' : 'bg-gradient-to-br from-indigo-50 to-purple-50'}`}>
        <div className="max-w-6xl mx-auto">
            <ProgressIndicator currentStep={5} />
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className={`${theme === 'dark' ? 'bg-gray-800 border border-border' : 'bg-white'} rounded-xl shadow-xl overflow-hidden mb-8`}
          >
            <div className={`${theme === 'dark' ? 'bg-primary' : 'bg-indigo-600'} h-2`}></div>
            <div className="p-8">
              <h1 className={`text-3xl font-bold text-center mb-4 ${theme === 'dark' ? 'text-card-foreground' : 'text-gray-900'}`}>
                {ONBOARDING_CONFIG.texts.selectGames.title}
              </h1>
              <p className={`text-lg text-center max-w-3xl mx-auto mb-8 ${theme === 'dark' ? 'text-muted-foreground' : 'text-gray-600'}`}>
                {ONBOARDING_CONFIG.texts.selectGames.description}
              </p>
              <div className={`${theme === 'dark' ? 'bg-muted/50 border border-border' : 'bg-indigo-50'} rounded-xl p-6 mb-8`}>
                <h3 className={`text-lg font-semibold mb-4 ${theme === 'dark' ? 'text-foreground' : 'text-indigo-900'}`}>Como interagir com os jogos:</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="flex items-center">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center mr-4 ${theme === 'dark' ? 'bg-green-600/20' : 'bg-green-100'}`}>
                      <BookOpen className={`h-6 w-6 ${theme === 'dark' ? 'text-green-400' : 'text-green-600'}`} />
                    </div>
                    <div>
                      <p className={`font-medium ${theme === 'dark' ? 'text-foreground' : 'text-gray-800'}`}>{ONBOARDING_CONFIG.texts.collectionHelp}</p>
                      <p className={`text-sm ${theme === 'dark' ? 'text-muted-foreground' : 'text-gray-600'}`}>Clique para adicionar ou remover da sua coleção</p>
                    </div>
                  </div>
                  <div className="flex items-center">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center mr-4 ${theme === 'dark' ? 'bg-red-600/20' : 'bg-red-100'}`}>
                      <Heart className={`h-6 w-6 ${theme === 'dark' ? 'text-red-400' : 'text-red-600'}`} />
                    </div>
                    <div>
                      <p className={`font-medium ${theme === 'dark' ? 'text-foreground' : 'text-gray-800'}`}>{ONBOARDING_CONFIG.texts.wishlistHelp}</p>
                      <p className={`text-sm ${theme === 'dark' ? 'text-muted-foreground' : 'text-gray-600'}`}>Clique para adicionar ou remover da sua lista de desejos</p>
                    </div>
                  </div>
                  <div className="flex items-center">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center mr-4 ${theme === 'dark' ? 'bg-blue-600/20' : 'bg-blue-100'}`}>
                      <PlusCircle className={`h-6 w-6 ${theme === 'dark' ? 'text-blue-400' : 'text-blue-600'}`} />
                    </div>
                    <div>
                      <p className={`font-medium ${theme === 'dark' ? 'text-foreground' : 'text-gray-800'}`}>{ONBOARDING_CONFIG.texts.playedHelp}</p>
                      <p className={`text-sm ${theme === 'dark' ? 'text-muted-foreground' : 'text-gray-600'}`}>Clique para marcar um jogo como jogado</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
          {loading && (
            <div className={`${theme === 'dark' ? 'bg-gray-800 border border-border' : 'bg-white'} text-center py-16 rounded-xl shadow-md`}>
              <div className={`inline-block h-16 w-16 animate-spin rounded-full border-4 border-solid border-r-transparent mb-6 ${theme === 'dark' ? 'border-primary' : 'border-indigo-600'}`}></div>
              <p className={`text-xl ${theme === 'dark' ? 'text-muted-foreground' : 'text-gray-600'}`}>Carregando jogos recomendados...</p>
            </div>
          )}
          {error && (
            <div className={`${theme === 'dark' ? 'bg-destructive/20 border-destructive text-destructive-foreground' : 'bg-red-50 border-red-400 text-red-800'} border-l-4 p-6 rounded-lg mb-6`}>
              <div className="flex">
                <div className="flex-shrink-0">
                  <XCircle className={`h-6 w-6 ${theme === 'dark' ? 'text-destructive' : 'text-red-400'}`} />
                </div>
                <div className="ml-3">
                  <p className={`text-lg font-medium ${theme === 'dark' ? '' : 'text-red-800'}`}>Ocorreu um erro</p>
                  <p className={`${theme === 'dark' ? 'text-sm' : 'text-red-700 mt-1'}`}>{error}</p>
                  <button 
                    onClick={() => window.location.reload()}
                    className={`mt-2 text-sm px-3 py-1 rounded-md transition-colors ${theme === 'dark' ? 'bg-destructive/30 hover:bg-destructive/40' : 'bg-red-100 text-red-800 hover:bg-red-200'}`}
                  >
                    Tentar novamente
                  </button>
                </div>
              </div>
            </div>
          )}
          {!loading && !error && (
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-8">
                {games.map((game, index) => {
                  const gameState = userGames.get(game.id) || { 
                    owned: false, 
                    wishlist: false, 
                    played: false, 
                    playCount: 0, 
                    rating: 0 
                  };
                    const gameForCard = (g: AppGame) => ({ ...g }); // Simplesmente espalha, assumindo que os tipos são compatíveis
                  return (
                    <motion.div
                      key={game.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3, delay: index * 0.05 }}
                    >
                      <GameCard
                        game={gameForCard(game)}
                        onToggleOwned={handleToggleOwned}
                        onToggleWishlist={handleToggleWishlist}
                        onTogglePlayed={handleTogglePlayed}
                        onUpdatePlayCount={handleUpdatePlayCount}
                        onRate={handleRateGame}
                        isInitiallyOwned={gameState.owned}
                        isInitiallyWishlist={gameState.wishlist}
                        isInitiallyPlayed={gameState.played}
                        user={user}
                      />
                    </motion.div>
                  );
                })}
              </div>
              <div className={`${theme === 'dark' ? 'bg-gray-800 border border-border' : 'bg-white'} rounded-xl p-8 shadow-md text-center`}>
                <div className="mb-6">
                  <CheckCircle2 className={`h-12 w-12 mx-auto mb-2 ${theme === 'dark' ? 'text-green-500' : 'text-green-600'}`} />
                  <p className={`text-lg ${theme === 'dark' ? 'text-muted-foreground' : 'text-gray-700'}`}>
                    {interactionCount > 0 
                      ? `Você já interagiu com ${interactionCount} ${interactionCount === 1 ? 'jogo' : 'jogos'}!` 
                      : 'Você pode prosseguir sem adicionar jogos agora, mas recomendamos selecionar pelo menos alguns para obter recomendações personalizadas.'}
                  </p>
                </div>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.98 }}
                    onClick={handleContinue} 
                    className={`px-8 py-4 text-lg font-medium rounded-xl shadow-md hover:shadow-lg transition-all flex items-center mx-auto ${theme === 'dark' ? 'bg-primary text-primary-foreground hover:bg-primary/90' : 'text-white bg-indigo-600 hover:bg-indigo-700'}`}
                >
                  {ONBOARDING_CONFIG.texts.buttonContinue} <ArrowRightCircle className="ml-2 h-5 w-5" />
                </motion.button>
              </div>
            </motion.div>
          )}
        </div>
      </div>
    </>
    );
  }

  // Fallback
  return (
    <div className={`min-h-screen flex items-center justify-center ${theme === 'dark' ? 'text-foreground' : ''}`}>
      <p>Carregando onboarding...</p>
    </div>
  );
};

export default OnboardingPage; 